import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import moment from 'moment'
import React from 'react'
import { i18n } from '../../domain/index'
import { Campaign } from '../types/campaigns'
import CampaignOfferModal from './CampaignOfferModal'

export function CampaignTable({ campaigns }: { campaigns: Campaign[] }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) // < 600px

  const [selectedCampaign, setSelectedCampaign] = React.useState<Campaign | null>(null)
  function onInfoClick(campaign: Campaign) {
    setSelectedCampaign(campaign)
  }
  function onModalClose() {
    setSelectedCampaign(null)
  }
  const sortedCampaigns = campaigns.slice(0).sort((campaign1, campaign2) => {
    const endTime1 = moment(campaign1.endTime)
    const endTime2 = moment(campaign2.endTime)
    return endTime1.isAfter(endTime2) ? 1 : endTime1.isBefore(endTime2) ? -1 : 0
  })

  // ---------- Mobile: card list ----------
  const renderMobileList = () => (
    <Stack component='ul' spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {sortedCampaigns.map((campaign) => (
        <Card component='li' key={campaign.id} elevation={1}>
          <CardHeader
            sx={{ pb: campaign.description ? 0 : 2 }}
            title={campaign.name}
            titleTypographyProps={{
              variant: 'subtitle1',
              sx: { wordBreak: 'break-word', lineHeight: 1.3 },
            }}
            subheader={
              <>
                {moment(campaign.startTime).format('D.M.YYYY')}
                {' – '}
                {moment(campaign.endTime).format('D.M.YYYY')}
              </>
            }
            subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            action={
              <IconButton size='small' onClick={() => onInfoClick(campaign)} aria-label='Tuotekuvaukset'>
                <InfoIcon fontSize='small' />
              </IconButton>
            }
          />
          {campaign.description && (
            <CardContent sx={{ pt: 1, '&:last-child': { pb: 2 } }}>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                {campaign.description}
              </Typography>
            </CardContent>
          )}
        </Card>
      ))}
    </Stack>
  )

  // ---------- Tablet + Desktop: table with horizontal scroll fallback ----------
  const renderTable = () => (
    <TableContainer
      component={Box}
      sx={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch', // smooth scrolling on iOS
      }}
    >
      <Table
        sx={{
          // smaller min-width so it fits tablets better; scrolls if needed
          minWidth: { xs: 520, md: 700 },
          // tighter cell padding on tablet
          '& .MuiTableCell-root': {
            px: { sm: 1, md: 2 },
            py: { sm: 1, md: 1.5 },
            fontSize: { sm: 13, md: 14 },
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t('campaigns.name')}</TableCell>
            <TableCell>{i18n.t('campaigns.description')}</TableCell>
            <TableCell>{i18n.t('campaigns.starts')}</TableCell>
            <TableCell>{i18n.t('campaigns.ends')}</TableCell>
            <TableCell sx={{ textAlign: 'center' }}>Tuotekuvaukset</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCampaigns.map((campaign) => (
            <TableRow key={campaign.id}>
              <TableCell>{campaign.name}</TableCell>
              <TableCell sx={{ minWidth: 140 }}>{campaign.description}</TableCell>
              <TableCell>{moment(campaign.startTime).format('D.M.YYYY')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{moment(campaign.endTime).format('D.M.YYYY')}</TableCell>
              <TableCell sx={{ width: 56, textAlign: 'center' }}>
                <IconButton size='small' onClick={() => onInfoClick(campaign)} aria-label='Tuotekuvaukset'>
                  <InfoIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <>
      {isMobile ? renderMobileList() : renderTable()}
      <CampaignOfferModal open={selectedCampaign !== null} campaign={selectedCampaign} onClose={onModalClose} />
    </>
  )
}
